import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

import useTranslations from '@/i18n/useTranslations';
import { formatArtworkTitleForDisplay } from '@/pages/artwork/utils/artworkTitleDisplay.utils';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import ViewInRoomEye from '@/shared-components/icons/components/artwork/ViewInRoomEye';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';

import {
  buildAndroidRoomIntent,
  buildLimitedEditionRoomShareUrl,
  getLimitedEditionRoomViewSource,
  getRoomViewPlatform,
} from './roomView.utils';

import './LimitedEditionRoomViewAction.css';

const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

export default function LimitedEditionRoomViewAction({ artworkData, selection, autoOpen = false, showTrigger = true }) {
  const t = useTranslations('common.shared.artwork_view.room_view');
  const [open, setOpen] = useState(autoOpen);
  const platform = getRoomViewPlatform();
  const source = getLimitedEditionRoomViewSource(artworkData, selection);
  const missing = [
    !selection?.size && { key: 'size', target: 'limited-edition-selection-size' },
    !selection?.frame && { key: 'frame', target: 'limited-edition-selection-frame' },
    !selection?.label && { key: 'label', target: 'limited-edition-selection-label' },
  ].filter(Boolean);
  const shareUrl = typeof window === 'undefined' || !source
    ? ''
    : buildLimitedEditionRoomShareUrl(window.location.origin, artworkData.slug, selection);
  const fallbackUrl = `${shareUrl}&room_edition_ar_unavailable=${encodeURIComponent(artworkData?.slug ?? '')}`;
  const fallbackFailed = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('room_edition_ar_unavailable') === artworkData?.slug;
  const title = formatArtworkTitleForDisplay(artworkData?.artwork_about?.artwork_title) || 'Artwork';
  const iosSupported = platform !== 'ios'
    || (typeof document !== 'undefined' && document.createElement('a').relList?.supports?.('ar'));

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [open]);

  const close = (event) => {
    event?.stopPropagation();
    setOpen(false);
  };
  const chooseOptions = () => {
    const target = missing[0]?.target;
    close();
    if (target) {
      requestAnimationFrame(() => {
        const node = document.getElementById(target);
        node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        node?.focus({ preventScroll: true });
      });
    }
  };

  let nextStep = null;
  if (fallbackFailed) {
    nextStep = <p>{t('unavailable')}</p>;
  } else if (missing.length) {
    const missingOptions = missing.map(({ key }) => key).join('_');
    nextStep = (
      <>
        <p>{t(`edition_choose_${missingOptions === 'size_frame_label' ? 'all' : missingOptions}_intro`)}</p>
        <ContinueButton type="button" onClick={chooseOptions}>
          {t('edition_choose_options')}
        </ContinueButton>
      </>
    );
  } else if (!source) {
    nextStep = <p>{t('edition_model_unavailable')}</p>;
  } else {
    const frameLabel = t(`edition_frame_${selection.frame}`);
    const labelLabel = t(selection.label === 'none' ? 'edition_label_none' : 'edition_label_yes');
    nextStep = (
      <>
        {platform === 'desktop' && (
          <>
            <p>{t('edition_desktop_instruction')}</p>
            <div className="artwork-room-view__qr"><QRCode value={shareUrl} size={180} /></div>
          </>
        )}
        <div className="limited-edition-room-view__summary">
          <span>{t('edition_step_size')}</span><strong>{source.width} × {source.height} cm</strong>
          <span>{t('edition_step_frame')}</span><strong>{frameLabel}</strong>
          <span>{t('edition_step_label')}</span><strong>{labelLabel}</strong>
        </div>
        {platform === 'ios' && iosSupported ? (
          <a
            href={`${source.usdzUrl}#allowsContentScaling=0`}
            rel="ar"
            className="continue-button__button limited-edition-room-view__launch"
            onClick={(event) => event.stopPropagation()}
          >
            <img className="artwork-room-view__quick-look-image" src={TRANSPARENT_PIXEL} alt="" />
            {t('edition_launch')}
          </a>
        ) : platform === 'android' ? (
          <a
            href={buildAndroidRoomIntent(source, fallbackUrl, title)}
            className="continue-button__button limited-edition-room-view__launch"
            onClick={(event) => event.stopPropagation()}
          >
            {t('edition_launch')}
          </a>
        ) : platform !== 'desktop' && <p>{t('unavailable')}</p>}
      </>
    );
  }

  return (
    <>
      {showTrigger && (
        <button
          type="button"
          className="limited-edition-room-view__trigger"
          onClick={(event) => { event.stopPropagation(); setOpen(true); }}
          aria-label={t('action')}
        >
          <ViewInRoomEye className="limited-edition-room-view__trigger-icon" />
          <span>{t('action')}</span>
        </button>
      )}
      {open && (
        <GenericPopup
          open={open}
          className="generic-popup_content remove-scrollbar"
          opacity
          onBackgroundClick={close}
        >
          <div className="generic-popup__header">
            <h2 className="generic-popup__header-text">{t('title')}</h2>
            <XIcon
              className="generic-popup__close"
              onClick={close}
              role="button"
              tabIndex={0}
              aria-label={t('close')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  close(event);
                }
              }}
            />
          </div>
          <div className="limited-edition-room-view__content">{nextStep}</div>
        </GenericPopup>
      )}
    </>
  );
}
